/**
 * EVO — xarid va import (procurement) ma'lumot qatlami.
 *
 * Tiplar `apps/api/src/modules/procurement/*.service.ts` dagi javob
 * interfeyslaridan MAYDONMA-MAYDON ko'chirilgan (`lib/inventory.ts` bilan bir
 * xil naqsh: bu qo'lda saqlanadigan shartnoma va server ustunni qayta nomlasa
 * TypeScript uni ushlamaydi — shuning uchun har o'zgarish jonli tekshiriladi).
 *
 * ============================================================================
 * IXTIYORIY MAYDON = RUXSAT YO'Q
 * ============================================================================
 * Server ruxsat bo'lmasa ustunni javobga UMUMAN qo'shmaydi, `null` ham qilmaydi:
 *
 *   `PurchaseOrder.fxRate/totalAmount/totalBase`   ← `supplier.view`
 *   `PurchaseOrderLine.unitPrice/lineAmount/…`     ← `supplier.view`
 *   `Shipment.supplierId/supplierName`             ← `supplier.view` (MATRIX §3.3 izoh⁷)
 *   `Shipment.shipmentCosts/totalCostBase`         ← `shipmentcost.view`
 *   `ShipmentCost.amount/fxRate/amountBase`        ← `shipmentcost.view`
 *   `GoodsReceiptLine.purchaseUnitCost/…`          ← `product.cost.view`
 *
 * Jonli tekshirilgan: `sotuvchi@evo.uz` uchun `GET /api/shipments/4` javobida
 * `supplierName` va `shipmentCosts` kalitlari UMUMAN YO'Q, `lines` esa bor.
 * `ombor@evo.uz` va `sotuvchi@evo.uz` uchun `/shipments/:id/costs` va
 * `/shipments/:id/landed-cost/preview` — 403.
 *
 * ============================================================================
 * PUL VA MIQDOR — `string`
 * ============================================================================
 * `"7172.4138"`, `"6.206897"`, `"500.0000"` — hammasi Postgres `numeric` dan
 * matn ko'rinishida keladi va SHUNDAY QOLADI. `parseFloat` taqiqlangan
 * (`lib/format.ts` fayl boshidagi qoida): landed cost invariantida oxirgi
 * xona 500 donaga ko'payib chiqadi (LANDED_COST §6.2).
 */
import { api } from './api';
import type { Page, SortDir } from './products';
/*
 * Tur ta'rifi `lib/suppliers.ts` da QOLADI va bu yerga ko'chirilmaydi:
 * ikkita `SUPPLIER_KINDS` ro'yxati bir kun bir-biridan farq qilib ketardi va
 * o'shanda tanlagich serverdan boshqa turlarni ko'rsatib turardi.
 */
import type { SupplierKind } from './suppliers';

// ---------------------------------------------------------------------------
// Umumiy
// ---------------------------------------------------------------------------

/** `procurement.common.ts` → `DateSchema`: hujjat sanasi kalendar sana. */
export type IsoDate = string;

interface BaseListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly sortBy?: string | undefined;
  readonly sortDir: SortDir;
  readonly q?: string | undefined;
  readonly from?: IsoDate | undefined;
  readonly to?: IsoDate | undefined;
}

// ---------------------------------------------------------------------------
// Xarid buyurtmasi (PO)
// ---------------------------------------------------------------------------

/** `purchase-orders.dto.ts` → `PO_STATUS_VALUES`. */
export const PO_STATUS_VALUES = [
  'draft',
  'submitted',
  'approved',
  'in_production',
  'shipped',
  'partially_received',
  'received',
  'closed',
  'cancelled',
] as const;
export type PoStatus = (typeof PO_STATUS_VALUES)[number];

/** `purchase-orders.service.ts` da tasdiqlash faqat shu holatlardan mumkin. */
export function isApprovablePo(status: string): boolean {
  return status === 'draft' || status === 'submitted';
}

/**
 * Bekor qilish mumkin bo'lgan holatlar — server bilan AYNAN bir xil
 * (`purchase-orders.service.ts` dagi `CANCELLABLE_STATUSES`).
 *
 * Chegara — TOVAR KELISHI. `partially_received`/`received` ni qabul
 * hujjati qo'yadi, ya'ni tovar allaqachon ombor daftariga tushgan va
 * landed cost hisoblangan. Hujjatni «bekor» deb belgilash o'sha qoldiqni,
 * tannarxni va COGS ni yo'q qilmaydi — faqat daftar bilan hujjatni
 * bir-biriga zid qilib qo'yadi.
 *
 * Ro'yxat ikki joyda takrorlangani ma'qul emas, lekin muqobili — server
 * javobida «bekor qilsa bo'ladimi» bayrog'ini yuborish, u esa qo'shimcha
 * shartnoma. Ro'yxat qisqa va o'zgarishi kam.
 */
export function isCancellablePo(status: string): boolean {
  return (
    status === 'draft' ||
    status === 'submitted' ||
    status === 'approved' ||
    status === 'in_production' ||
    status === 'shipped'
  );
}

export interface PurchaseOrderLine {
  readonly id: number;
  readonly productId: number;
  readonly sku: string;
  readonly name: string;
  readonly uomCode: string | null;
  readonly quantity: string;
  readonly receivedQuantity: string;
  /** `quantity − receivedQuantity` — yana nima kutilmoqda. */
  readonly outstandingQuantity: string;
  readonly notes: string | null;
  /** FAQAT `supplier.view` bilan keladi. Hujjat valyutasida (odatda CNY). */
  readonly unitPrice?: string;
  readonly lineAmount?: string;
  readonly lineBase?: string;
}

export interface PurchaseOrderHeader {
  readonly id: number;
  readonly docNo: string;
  readonly supplierId: number;
  readonly supplierName: string | null;
  readonly legalEntityId: number;
  readonly orderDate: IsoDate;
  readonly expectedDate: IsoDate | null;
  readonly currencyCode: string;
  readonly status: string;
  readonly notes: string | null;
  readonly submittedAt: string | null;
  readonly approvedAt: string | null;
  readonly approvedBy: number | null;
  /**
   * Bekor qilish izi. `cancelled` holatida IKKALASI ham to'ldirilgan —
   * bazada CHECK bilan ta'minlangan (`ck_purchase_orders_cancel_complete`),
   * ya'ni «bekor qilingan, lekin sababi yo'q» holati mumkin emas.
   */
  readonly cancelledAt: string | null;
  readonly cancelReason: string | null;
  /**
   * ZAVOD TAYYORLAYDIGAN KUN — `expectedDate` (kelish) DAN BOSHQA SANA.
   *
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ Import zanjirida uch sana bor va ular MUSTAQIL siljiydi:              │
   * │   `readyDate`   — zavod ishlab chiqarib bo'ladigan kun (~40 kun)      │
   * │   `Shipment.eta`— konteynerning kelishi (~90-110 kun)                 │
   * │   `expectedDate`— buyurtma bo'yicha kutilgan kelish                   │
   * │                                                                       │
   * │ Bittasi bilan yuritilsa kechikish SABABI yo'qoladi: zavod             │
   * │ kechiktirdimi yoki kema kechikdimi — javob bo'lmaydi.                 │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * `supplier.view` ostida EMAS: bu narx emas, kuzatuv ma'lumoti va omborchi
   * ham ko'radi («bu tovar qachon tayyor bo'ladi» — qabulga tayyorgarlik).
   */
  readonly readyDate: IsoDate | null;
  /** FAKT. `readyDate` bilan farqi — zavodning ishonchliligi o'lchovi. */
  readonly actualReadyDate: IsoDate | null;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  /**
   * HUJJAT KURSI — bugungi kurs EMAS.
   *
   * `FxService.rateForDocument()` buyurtma sanasida qotirib qo'yadi va USD
   * ekvivalenti AYNAN shu kurs bilan hisoblanadi. Ekranda bugungi kursni
   * ishlatish 4 oy oldingi hujjatni bugungi kursda ko'rsatib, «tannarx o'sib
   * ketdi» degan soxta xulosa beradi (LANDED_COST §6.7 bilan bir xil sabab).
   */
  readonly fxRate?: string;
  readonly totalAmount?: string;
  readonly totalBase?: string;
}

export interface PurchaseOrder extends PurchaseOrderHeader {
  readonly lines: readonly PurchaseOrderLine[];
}

export interface PurchaseOrderListParams extends BaseListParams {
  readonly supplierId?: number | undefined;
  readonly status?: PoStatus | undefined;
  readonly includeCancelled?: boolean | undefined;
}

export function fetchPurchaseOrders(
  params: PurchaseOrderListParams,
  signal?: AbortSignal,
): Promise<Page<PurchaseOrderHeader>> {
  return api.get<Page<PurchaseOrderHeader>>('/purchase-orders', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy,
      sortDir: params.sortDir,
      q: params.q,
      supplierId: params.supplierId,
      status: params.status,
      from: params.from,
      to: params.to,
      // `BooleanFlag` — server `'true'`/`'false'` MATNINI kutadi.
      includeCancelled: params.includeCancelled === true ? 'true' : undefined,
    },
    ...(signal ? { signal } : {}),
  });
}

export function fetchPurchaseOrder(id: number, signal?: AbortSignal): Promise<PurchaseOrder> {
  return api.get<PurchaseOrder>(`/purchase-orders/${id}`, { ...(signal ? { signal } : {}) });
}

export interface PurchaseOrderLineInput {
  readonly productId: number;
  readonly quantity: string;
  readonly unitPrice: string;
  readonly notes?: string | null;
}

/**
 * `fxRate` va `totalAmount` so'rov tanasida YO'Q va bo'lishi ham mumkin emas:
 * kurs `FxService` dan olinadi, jami esa satrlardan `@evo/core` bilan
 * hisoblanadi (`purchase-orders.dto.ts` izohi). Mijozdan kurs qabul qilish —
 * hujjatni ixtiyoriy kurs bilan yozib, tannarxni jimgina o'zgartirish yo'li.
 */
export interface PurchaseOrderCreateInput {
  readonly supplierId: number;
  readonly legalEntityId: number;
  readonly orderDate: IsoDate;
  readonly expectedDate?: IsoDate | null;
  /**
   * REJA sanasi. `actualReadyDate` bu yerda YO'Q va bo'lishi ham mumkin
   * emas: buyurtma yaratilayotganda zavod hali hech narsa ishlab
   * chiqarmagan. Faktni yozish uchun `setPurchaseOrderReadyDates()`.
   */
  readonly readyDate?: IsoDate | null;
  readonly currencyCode: string;
  readonly notes?: string | null;
  readonly lines: readonly PurchaseOrderLineInput[];
}

export function createPurchaseOrder(input: PurchaseOrderCreateInput): Promise<PurchaseOrder> {
  return api.post<PurchaseOrder>('/purchase-orders', input);
}

/** Tasdiqlash — `po.approve`. Izoh ixtiyoriy, lekin auditga tushadi. */
export function approvePurchaseOrder(id: number, notes?: string | null): Promise<PurchaseOrder> {
  return api.post<PurchaseOrder>(`/purchase-orders/${id}/approve`, notes ? { notes } : {});
}

/**
 * Bekor qilish — `po.cancel`.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN ALOHIDA ROUTE, `PATCH {"status":"cancelled"}` EMAS             │
 * │                                                                           │
 * │ Avval holat umumiy `PATCH` orqali yozilardi — va aynan o'sha yo'l         │
 * │ `po.approve` ni chetlab o'tish teshigi bo'lib chiqdi: `po.create`         │
 * │ ruxsati bor moliyachi `{"status":"approved"}` yuborib o'z buyurtmasini    │
 * │ o'zi tasdiqlab olardi, tasdiqlash izisiz va outbox hodisasisiz.           │
 * │                                                                           │
 * │ Shuning uchun `status` DTO dan butunlay olib tashlandi va har bir holat   │
 * │ o'tishi o'z route'ini oldi: u yerda ruxsat, audit va outbox birga.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `reason` MAJBURIY (server: kamida 3 belgi): tasdiqlangan buyurtmani bekor
 * qilish ta'minotchi bilan olingan majburiyatni buzadi va bu keyin albatta
 * savol tug'diradi — javob hujjatning o'zida turishi kerak.
 *
 * `version` ham majburiy: bekor qilish qaytarib bo'lmaydi, shuning uchun
 * mijoz AYNAN qaysi holatni ko'rganini bildirishi shart (optimistik qulf).
 */
export function cancelPurchaseOrder(
  id: number,
  input: { readonly reason: string; readonly version: number },
): Promise<PurchaseOrder> {
  return api.post<PurchaseOrder>(`/purchase-orders/${id}/cancel`, input);
}

/**
 * TAYYOR BO'LISH SANALARI — reja va fakt.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN ALOHIDA ENDPOINT, `PATCH /:id` EMAS                            │
 * │                                                                           │
 * │ Umumiy `PATCH` faqat `draft`/`submitted` holatida ishlaydi. Zavod esa     │
 * │ «10 kun kechikamiz» yoki «bugun tayyor» deganda buyurtma allaqachon       │
 * │ `approved`/`in_production` bo'ladi — ya'ni sana HECH QACHON kiritilmasdi  │
 * │ va butun mexanizm (kechikish hisobi, «tayyor bo'lgandan N kun keyin»      │
 * │ to'lovi, ta'minotchi ko'rsatkichi) ishlamay qolardi.                      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `null` — «o'chir», maydonni umuman yubormaslik — «tegma». Ikkalasi ajratilgan:
 * xato kiritilgan fakt sanasini qaytarib olish kerak bo'ladi, lekin rejani
 * yangilashda faktni tasodifan tozalab yuborish mumkin emas.
 */
export function setPurchaseOrderReadyDates(
  id: number,
  input: {
    readonly readyDate?: IsoDate | null;
    readonly actualReadyDate?: IsoDate | null;
    readonly version?: number;
  },
): Promise<PurchaseOrder> {
  return api.post<PurchaseOrder>(`/purchase-orders/${id}/ready-dates`, input);
}

/**
 * Zavod aytgan kun bilan haqiqiy kun orasidagi farq (kun).
 *
 * Musbat — zavod kechikdi, manfiy — erta tayyorladi. `null` — hali
 * o'lchanmagan (ikkala sana ham bo'lishi shart).
 *
 * `delayDays()` (pastda, ETA↔ATA uchun) bilan bir xil arifmetika, lekin
 * ALOHIDA funksiya: ikkalasi turli SAVOLGA javob beradi va ularni bitta
 * funksiyaga birlashtirish chaqiruv joyida «qaysi kechikish?» degan
 * chalkashlik tug'dirardi — aynan shu farqni ko'rsatish uchun ikki sana
 * juftligi bazaga qo'shilgan.
 */
export function factoryDelayDays(order: {
  readonly readyDate: IsoDate | null;
  readonly actualReadyDate: IsoDate | null;
}): number | null {
  return delayDays(order.readyDate, order.actualReadyDate);
}

// ---------------------------------------------------------------------------
// To'lov jadvali (PO payment terms)
// ---------------------------------------------------------------------------

/**
 * Muddat NIMAGA nisbatan hisoblanadi — `po_payment_terms.due_basis`.
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║ MUDDAT SANAGA EMAS, HODISAGA BOG'LANADI                                   ║
 * ║                                                                           ║
 * ║ «Jo'natishdan 5 kun OLDIN» = `shipment` + `offsetDays: -5`.               ║
 * ║                                                                           ║
 * ║ Konteyner jo'natilish sanasi buyurtma yozilganda NOMA'LUM va u keyin      ║
 * ║ bir necha marta siljiydi. Sanani qo'lda yozib qo'yish har siljishda uni   ║
 * ║ QO'LDA tuzatishni talab qilardi, unutilgan bitta tuzatish esa «to'lov     ║
 * ║ muddati o'tdi» degan YOLG'ON ogohlantirish berardi.                       ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */
export const PO_PAYMENT_BASIS_VALUES = ['order', 'ready', 'shipment', 'arrival'] as const;
export type PoPaymentBasis = (typeof PO_PAYMENT_BASIS_VALUES)[number];

export const PO_PAYMENT_TERM_STATUS_VALUES = ['planned', 'due', 'paid', 'cancelled'] as const;
export type PoPaymentTermStatus = (typeof PO_PAYMENT_TERM_STATUS_VALUES)[number];

/** Asos sanasi QAYSI ustundan olindi — ekranda «nega bu sana?» javobi. */
export type PoPaymentBasisSource =
  | 'order_date'
  | 'ready_date'
  | 'actual_ready_date'
  | 'shipment_etd'
  | 'shipment_atd'
  | 'shipment_eta'
  | 'shipment_ata';

export interface PaymentTerm {
  readonly id: number;
  readonly seq: number;
  readonly label: string;
  /** Ulush YOKI summa — ikkalasi emas (bazada XOR cheklovi). */
  readonly percent: string | null;
  /** Hujjat valyutasida (odatda CNY). */
  readonly amount: string | null;
  /** Baza valyutada (USD) — taqvim va ogohlantirishlar shu ustunda ishlaydi. */
  readonly amountBase: string;
  readonly paidAmount: string;
  readonly status: string;
  readonly dueBasis: string;
  /** MANFIY bo'lishi mumkin: «jo'natishdan 5 kun oldin» = −5. */
  readonly offsetDays: number;
  /**
   * HISOBLANGAN muddat.
   *
   * `null` — asos sanasi hali noma'lum (konteyner yo'q, tayyor bo'lish kuni
   * kiritilmagan). Ekran bu holatni «—» emas, OCHIQ tushuntirish bilan
   * ko'rsatadi: taxminiy sana chizish pul rejasini yolg'on qilardi.
   */
  readonly dueDate: IsoDate | null;
  readonly basisDate: IsoDate | null;
  readonly basisSource: PoPaymentBasisSource | null;
  /** Musbat — muddati o'tgan kunlar. `null` — muddat kelmagan yoki noma'lum. */
  readonly overdueDays: number | null;
  readonly notes: string | null;
  readonly version: number;
}

export interface PaymentTermsResponse {
  readonly purchaseOrderId: number;
  readonly docNo: string;
  readonly currencyCode: string;
  readonly totalAmount: string;
  readonly totalBase: string;
  readonly terms: readonly PaymentTerm[];
  readonly totals: {
    readonly percentTotal: string;
    readonly amountBaseTotal: string;
    readonly undatedCount: number;
  };
  /**
   * Shu o'qishda nechta muddat QAYTA HISOBLANDI.
   *
   * Odatda 0. Noldan katta — «asos sanasi siljibdi» degani va ekran buni
   * ochiq aytadi, aks holda sana jimgina o'zgargandek tuyulardi.
   */
  readonly recalculated: number;
}

export interface PaymentTermInput {
  readonly label: string;
  readonly percent?: string | null;
  readonly amount?: string | null;
  readonly dueBasis: PoPaymentBasis;
  readonly offsetDays: number;
  readonly notes?: string | null;
}

export function fetchPaymentTerms(
  purchaseOrderId: number,
  signal?: AbortSignal,
): Promise<PaymentTermsResponse> {
  return api.get<PaymentTermsResponse>(`/purchase-orders/${purchaseOrderId}/payment-terms`, {
    ...(signal ? { signal } : {}),
  });
}

/**
 * BUTUN jadvalni almashtiradi.
 *
 * Satrma-satr tahrirlash oraliqda 30% + 30% (jami 60%) kabi MA'NOSIZ
 * holatlarni yaratardi va o'sha oraliqda to'lov taqvimi yolg'on raqam
 * ko'rsatib turardi. Taqvim esa pul rejasi — u bir soniya ham yolg'on
 * bo'lmasligi kerak.
 */
export function replacePaymentTerms(
  purchaseOrderId: number,
  terms: readonly PaymentTermInput[],
): Promise<PaymentTermsResponse> {
  return api.put<PaymentTermsResponse>(`/purchase-orders/${purchaseOrderId}/payment-terms`, {
    terms,
  });
}

/** Bitta bosqich qo'shadi. `seq` ni SERVER beradi (poyga himoyasi bilan). */
export function addPaymentTerm(
  purchaseOrderId: number,
  term: PaymentTermInput,
): Promise<PaymentTermsResponse> {
  return api.post<PaymentTermsResponse>(
    `/purchase-orders/${purchaseOrderId}/payment-terms`,
    term,
  );
}

export function updatePaymentTerm(
  purchaseOrderId: number,
  termId: number,
  term: PaymentTermInput & {
    readonly status?: PoPaymentTermStatus;
    readonly paidAmount?: string;
  },
): Promise<PaymentTermsResponse> {
  return api.put<PaymentTermsResponse>(
    `/purchase-orders/${purchaseOrderId}/payment-terms/${termId}`,
    term,
  );
}

export function deletePaymentTerm(
  purchaseOrderId: number,
  termId: number,
): Promise<PaymentTermsResponse> {
  return api.delete<PaymentTermsResponse>(
    `/purchase-orders/${purchaseOrderId}/payment-terms/${termId}`,
  );
}

// ---------------------------------------------------------------------------
// Ta'minot bo'limining ish maydoni
// ---------------------------------------------------------------------------

export type AttentionKind =
  | 'payment_overdue'
  | 'payment_due_soon'
  | 'production_late'
  | 'arrival_late';

export interface AttentionRow {
  readonly kind: AttentionKind;
  readonly count: number;
  /** Pul o'lchovi bo'lmagan qatorlarda `null` (kechikkan ishlab chiqarish). */
  readonly amountBase: string | null;
}

export interface PipelineStage {
  readonly status: string;
  readonly count: number;
  readonly totalBase: string;
}

export interface PaymentCalendarRow {
  readonly dueDate: IsoDate;
  readonly supplierId: number;
  readonly supplierName: string | null;
  readonly count: number;
  readonly amountBase: string;
}

export interface OverduePaymentRow {
  readonly termId: number;
  readonly purchaseOrderId: number;
  readonly docNo: string;
  readonly supplierName: string | null;
  readonly label: string;
  readonly dueDate: IsoDate;
  readonly overdueDays: number;
  readonly amountBase: string;
}

export interface SupplierReliabilityRow {
  readonly supplierId: number;
  readonly supplierName: string | null;
  /** Nechta buyurtmada REJA ham, FAKT ham bor — o'lchov shunga tayanadi. */
  readonly measuredOrders: number;
  /** O'rtacha kechikish (kun). Musbat — zavod kechikadi. */
  readonly avgDelayDays: string | null;
  readonly worstDelayDays: number | null;
  /** HOZIR muddati o'tgan, lekin hali tayyor bo'lmagan buyurtmalar. */
  readonly lateNow: number;
}

export interface ProcurementWorkspace {
  readonly asOf: IsoDate;
  readonly recalculated: number;
  /** FAQAT nolga teng bo'lmagan qatorlar — server ularni o'zi filtrlaydi. */
  readonly attention: readonly AttentionRow[];
  readonly pipeline: readonly PipelineStage[];
  readonly paymentCalendar: {
    readonly horizonDays: number;
    readonly totalBase: string;
    readonly rows: readonly PaymentCalendarRow[];
  };
  readonly overduePayments: readonly OverduePaymentRow[];
  readonly supplierReliability: readonly SupplierReliabilityRow[];
}

/**
 * Ish maydoni — `po.view` VA `supplier.view` talab qiladi.
 *
 * Ikkinchisi `SALES_FORBIDDEN_CODES` ro'yxatida (seed uni sotuvchiga
 * berishni taqiqlaydi), ya'ni sotuvchi bu ekranni hech qachon ko'rmaydi —
 * bugun ham, kelajakda ham.
 */
export function fetchProcurementWorkspace(signal?: AbortSignal): Promise<ProcurementWorkspace> {
  return api.get<ProcurementWorkspace>('/procurement/workspace', {
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// Jo'natma (konteyner)
// ---------------------------------------------------------------------------

/** `shipments.dto.ts` → `SHIPMENT_STATUS_VALUES`. */
export const SHIPMENT_STATUS_VALUES = [
  'planned',
  'in_transit',
  'at_customs',
  'cleared',
  'received',
  'closed',
] as const;
export type ShipmentStatus = (typeof SHIPMENT_STATUS_VALUES)[number];

/** `shipments.lc_status`: tannarx hali hisoblanmagan / taxminiy / yakuniy. */
export const LC_STATUS_VALUES = ['pending', 'estimated', 'final'] as const;
export type LcStatus = (typeof LC_STATUS_VALUES)[number];

export interface ShipmentLine {
  readonly id: number;
  readonly productId: number;
  readonly purchaseOrderLineId: number | null;
  readonly sku: string;
  readonly name: string;
  readonly uomCode: string | null;
  readonly quantity: string;
  readonly reservedQuantity: string;
  /**
   * ┌──────────────────────────────────────────────────────────────────────┐
   * │ «Bu konteynerdan 340/500 dona sotilgan» — GAP_ANALYSIS §A1.          │
   * │                                                                      │
   * │ Bu ko'rsatkich emas, KEYINGI XARID QARORI: konteyner kelishidan     │
   * │ oldin 100% sotilgan bo'lsa — KAM buyurtma qilingan, pul stolda       │
   * │ qolgan. 20% sotilgan bo'lsa — kapital muzlagan.                      │
   * └──────────────────────────────────────────────────────────────────────┘
   */
  readonly soldQuantity: string;
  /** `quantity − reservedQuantity` — hali sotilmagan, erkin qism. */
  readonly freeQuantity: string;
  /**
   * FAKTIK o'lchov: qadoq boshqacha bo'lsa mahsulot kartochkasidan farq qiladi.
   * FAQAT `shipment.edit` — ruxsatsiz javobda kalit UMUMAN yo'q (`null` emas).
   *
   * `weightKg` — BIR DONAGA **BRUTTO** (o'ram bilan), `netWeightKg` — bir
   * donaga **NETTO** (o'ramsiz, 0055). Ikkalasi ham satr JAMISI EMAS:
   * satr jamisi = miqdor × shu qiymat (`schema/procurement.ts` dagi ramka).
   */
  readonly weightKg?: string | null;
  readonly netWeightKg?: string | null;
  readonly volumeM3?: string | null;
  readonly notes?: string | null;
}

/**
 * Partiyaning BOJXONA JAMISI — `shipments.service.ts` → `customsTotals` (0055).
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ `null` — «MA'LUM EMAS», NOL EMAS                                         │
 * │                                                                          │
 * │ Birorta satrda ham o'lchov ko'rsatilmagan bo'lsa, server nol emas        │
 * │ `null` qaytaradi. Ekran uni `formatNumber(null)` bilan `—` deb chizadi:  │
 * │ bojxona hujjatida «0 kg» bilan «o'lchanmagan» ORASIDAGI farq yuridik.    │
 * │                                                                          │
 * │ `missing*Lines` esa aralash holatni aytadi — jami bor, lekin u nechta    │
 * │ satrni HISOBGA OLMAGAN. Bu raqamsiz jami o'zini to'liq ko'rsatardi.      │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export interface ShipmentCustomsTotals {
  readonly quantity: string;
  readonly cartonCount: number;
  readonly grossWeightKg: string | null;
  readonly netWeightKg: string | null;
  readonly volumeM3: string | null;
  readonly missingGrossLines: number;
  readonly missingNetLines: number;
  readonly missingVolumeLines: number;
}

/**
 * Jo'natma sarlavhasi — UCH QATLAMLI proyeksiya.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ `?` — «QIYMAT YO'Q» EMAS, «SIZGA TEGISHLI EMAS»                          │
 * │                                                                          │
 * │   `shipment.view`   id, docNo, eta, status, version, createdAt, updatedAt│
 * │ + `shipment.edit`   containerNo, blNo, departurePort, arrivalPort, etd,  │
 * │                     atd, ata, transitWarehouseId, lcStatus, notes        │
 * │ + `supplier.view`   supplierId, supplierName                             │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * NIMA UCHUN `blNo` VA `containerNo` TA'MINOTCHI BILAN BIR QATORDA:
 * konosament raqami va konteyner raqami ochiq treking tizimlariga kiritiladi
 * va o'sha yerda SHIPPER, ya'ni ta'minotchi ko'rinadi. `supplierName` ni
 * yashirib bularni ochiq qoldirish — qulflangan eshik yonidagi ochiq deraza.
 *
 * TIP DARAJASIDA `?`: server maydonni `null` qilmaydi, javobdan BUTUNLAY
 * olib tashlaydi. `string | null` deb yozilsa TypeScript ruxsatsiz holatni
 * umuman ko'rmasdi va ekran `undefined` ni `—` deb chizib, «ma'lumot
 * kiritilmagan» degan YOLG'ON xabar berardi. Jonli tekshirilgan:
 * `sotuvchi@evo.uz` javobida bu kalitlar yo'q, `ombor@evo.uz` da bor.
 */
export interface ShipmentHeader {
  readonly id: number;
  readonly docNo: string;
  readonly eta: IsoDate | null;
  readonly status: string;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;

  /** --- `shipment.edit` qatlami --- */
  readonly containerNo?: string | null;
  readonly blNo?: string | null;
  readonly departurePort?: string | null;
  readonly arrivalPort?: string | null;
  /** Reja (`eta` yuqorida — u sotuvchiga ham ochiq: «qachon keladi?»). */
  readonly etd?: IsoDate | null;
  /** Fakt. */
  readonly atd?: IsoDate | null;
  readonly ata?: IsoDate | null;
  readonly transitWarehouseId?: number | null;
  readonly lcStatus?: string;
  readonly notes?: string | null;

  /** --- `supplier.view` qatlami (MATRIX §3.3 izoh⁷) --- */
  readonly supplierId?: number | null;
  readonly supplierName?: string | null;
}

export interface Shipment extends ShipmentHeader {
  readonly lines: readonly ShipmentLine[];
  /** FAQAT `shipmentcost.view` — aks holda kalit umuman yo'q. */
  readonly shipmentCosts?: readonly ShipmentCost[];
  readonly totalCostBase?: string;
  /**
   * FAQAT `shipment.edit` — satrdagi o'lchov ustunlari BILAN BIR XIL qatlamda.
   *
   * Server ataylab shunday qildi: jami ochiq qolsa, yopilgan `weightKg`
   * ustunlari jami orqali qayta tiklanardi (`shipments.service.ts` izohi).
   */
  readonly customsTotals?: ShipmentCustomsTotals;
}

/**
 * Ro'yxat satri + o'sha yukning SATRLARI.
 *
 * `GET /api/shipments` sarlavhalarni qaytaradi, «sotilgan / erkin» esa faqat
 * `GET /api/shipments/:id` javobidagi satrlarda bor. §A1 aynan shu nisbatni
 * talab qiladi: usiz ro'yxat «qachon keladi» degan jadval bo'lib qoladi va
 * «yana buyurtma berish kerakmi?» degan savolga javob bermaydi.
 *
 * `lines: null` — satrlarni yuklab bo'lmadi (masalan tarmoq uzildi). Bo'sh
 * massiv bilan ADASHTIRMASLIK kerak: bo'sh massiv «satr yo'q» degani.
 */
export interface ShipmentWithLines extends ShipmentHeader {
  readonly lines: readonly ShipmentLine[] | null;
}

export interface ShipmentListParams extends BaseListParams {
  readonly supplierId?: number | undefined;
  readonly status?: ShipmentStatus | undefined;
  readonly lcStatus?: LcStatus | undefined;
  readonly includeClosed?: boolean | undefined;
}

export function fetchShipments(
  params: ShipmentListParams,
  signal?: AbortSignal,
): Promise<Page<ShipmentHeader>> {
  return api.get<Page<ShipmentHeader>>('/shipments', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy,
      sortDir: params.sortDir,
      q: params.q,
      supplierId: params.supplierId,
      status: params.status,
      lcStatus: params.lcStatus,
      from: params.from,
      to: params.to,
      includeClosed: params.includeClosed === true ? 'true' : undefined,
    },
    ...(signal ? { signal } : {}),
  });
}

export function fetchShipment(id: number, signal?: AbortSignal): Promise<Shipment> {
  return api.get<Shipment>(`/shipments/${id}`, { ...(signal ? { signal } : {}) });
}

/**
 * Ro'yxat + har satr uchun kartochka.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN N TA SO'ROV                                                   │
 * │                                                                          │
 * │ Bu `lib/sales.ts` dagi `fetchOpenShipments()` bilan bir xil murosа va    │
 * │ bir xil sabab: kerakli ma'lumot (erkin miqdor) FAQAT kartochkada bor.    │
 * │ Bu yerda so'rovlar soni SAHIFA O'LCHAMI bilan cheklangan (25), ya'ni     │
 * │ ekran hech qachon 25 tadan ortiq so'rov yubormaydi.                      │
 * │                                                                          │
 * │ Bitta kartochka yiqilsa butun ro'yxat yiqilmaydi: o'sha satrning         │
 * │ `lines` maydoni `null` bo'lib qoladi va ekran «yuklab bo'lmadi» deydi.   │
 * │ Aks holda bitta 403 (masalan yangi ombor ko'lami) butun jadvalni         │
 * │ o'chirib qo'yardi.                                                       │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export async function fetchShipmentsWithLines(
  params: ShipmentListParams,
  signal?: AbortSignal,
): Promise<Page<ShipmentWithLines>> {
  const page = await fetchShipments(params, signal);

  const details = await Promise.all(
    page.items.map(async (header): Promise<ShipmentWithLines> => {
      try {
        const detail = await fetchShipment(header.id, signal);
        return { ...header, lines: detail.lines };
      } catch {
        return { ...header, lines: null };
      }
    }),
  );

  return { ...page, items: details };
}

export interface ShipmentLineInput {
  readonly productId: number;
  readonly purchaseOrderLineId?: number | null;
  readonly quantity: string;
  /** BIR DONAGA brutto (o'ram bilan) va netto (o'ramsiz) — 0055. */
  readonly weightKg?: string | null;
  readonly netWeightKg?: string | null;
  readonly volumeM3?: string | null;
  readonly notes?: string | null;
}

export interface ShipmentCreateInput {
  readonly supplierId?: number | null;
  readonly legalEntityId: number;
  readonly containerNo?: string | null;
  readonly blNo?: string | null;
  readonly departurePort?: string | null;
  readonly arrivalPort?: string | null;
  readonly etd?: IsoDate | null;
  readonly eta?: IsoDate | null;
  readonly atd?: IsoDate | null;
  readonly ata?: IsoDate | null;
  readonly status?: ShipmentStatus;
  readonly notes?: string | null;
  readonly lines: readonly ShipmentLineInput[];
}

export function createShipment(input: ShipmentCreateInput): Promise<Shipment> {
  return api.post<Shipment>('/shipments', input);
}

/**
 * Holatni o'zgartirish — MATRIX §3.3 izoh⁸: omborchida `shipment.edit` FAQAT
 * shu o'tish uchun (`at_customs` → `cleared` → `received`). Cheklovni server
 * qo'yadi, ekran esa faqat tugmani ko'rsatadi.
 */
export function updateShipmentStatus(
  id: number,
  status: ShipmentStatus,
  version?: number,
): Promise<Shipment> {
  return api.patch<Shipment>(`/shipments/${id}`, {
    status,
    ...(version === undefined ? {} : { version }),
  });
}

// ---------------------------------------------------------------------------
// Jo'natma xarajatlari
// ---------------------------------------------------------------------------

/** `shipments.dto.ts` → `ALLOC_METHOD_VALUES`. */
export const ALLOC_METHOD_VALUES = ['value', 'quantity', 'weight', 'volume', 'manual'] as const;
export type AllocMethod = (typeof ALLOC_METHOD_VALUES)[number];

export interface ShipmentCost {
  readonly id: number;
  readonly costTypeId: number;
  readonly costTypeCode: string | null;
  readonly costTypeName: string | null;
  /**
   * XARAJAT KIMGA TEGISHLI — «logistikaning uchoti» (0054).
   *
   * `null` ODATIY: bojxona boji DAVLATGA to'lanadi (kontragent ham, nasiya
   * ham yo'q) va 0054 dan oldingi qatorlarda kim ekani noma'lum.
   * Bu ustunning O'ZI qarz YARATMAYDI — qarz `supplier_invoices` hujjati
   * bilan tug'iladi.
   */
  readonly supplierId: number | null;
  readonly description: string | null;
  readonly currencyCode: string;
  readonly allocMethod: string;
  /** `true` = taxminiy (hujjat hali kelmagan) — LANDED_COST §4. */
  readonly isEstimate: boolean;
  readonly documentRef: string | null;
  /** Kurs AYNAN shu sanadan olinadi (LANDED_COST §3.3). */
  readonly incurredDate: IsoDate;
  readonly createdAt: string;
  /** FAQAT `shipmentcost.view`. */
  readonly amount?: string;
  readonly fxRate?: string;
  readonly amountBase?: string;
}

export interface ShipmentCostInput {
  readonly costTypeId: number;
  /** Kontragent — ixtiyoriy (0054). Boj/yig'im uchun berilmaydi. */
  readonly supplierId?: number | null;
  readonly description?: string | null;
  readonly amount: string;
  readonly currencyCode: string;
  readonly allocMethod?: AllocMethod;
  readonly isEstimate?: boolean;
  readonly documentRef?: string | null;
  readonly incurredDate: IsoDate;
}

export function fetchShipmentCosts(
  shipmentId: number,
  signal?: AbortSignal,
): Promise<readonly ShipmentCost[]> {
  return api.get<readonly ShipmentCost[]>(`/shipments/${shipmentId}/costs`, {
    ...(signal ? { signal } : {}),
  });
}

/** Javob — YANGILANGAN TO'LIQ ro'yxat, bitta qator emas. */
export function addShipmentCost(
  shipmentId: number,
  input: ShipmentCostInput,
): Promise<readonly ShipmentCost[]> {
  return api.post<readonly ShipmentCost[]>(`/shipments/${shipmentId}/costs`, input);
}

/**
 * Mavjud xarajat qatorini tuzatish (`PATCH /shipments/:id/costs/:costId`).
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN BU FUNKSIYA ENDI KERAK BO'LDI                                 │
 * │                                                                          │
 * │ `supplierId` ustuni 0054 da paydo bo'ldi, ya'ni undan OLDIN kiritilgan   │
 * │ har bir xarajat qatorida u `null`. Faqat «qo'shish» formasi bo'lsa,      │
 * │ eski qatorlarni kontragentga bog'lashning YAGONA yo'li — ularni          │
 * │ o'chirib qayta yozish bo'lardi, va bu tannarx taqsimlashini buzardi.     │
 * │                                                                          │
 * │ `costTypeId` bu yerda YO'Q — server ham qabul qilmaydi                   │
 * │ (`UpdateShipmentCostSchema`): turni jimgina almashtirish auditda         │
 * │ «boj → bank komissiyasi» degan tushunarsiz iz qoldirardi.                │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ Server BO'SH so'rovni rad etadi (`.refine(Object.keys(dto).length > 0)`),
 * shuning uchun chaqiruvchi kamida bitta maydon yuborishi shart.
 *
 * Javob — `addShipmentCost` bilan bir xil: YANGILANGAN TO'LIQ ro'yxat.
 */
export function updateShipmentCost(
  shipmentId: number,
  costId: number,
  patch: ShipmentCostPatch,
): Promise<readonly ShipmentCost[]> {
  return api.patch<readonly ShipmentCost[]>(`/shipments/${shipmentId}/costs/${costId}`, patch);
}

/** `UpdateShipmentCostSchema` — `costTypeId` ATAYLAB yo'q (yuqoridagi izoh). */
export interface ShipmentCostPatch {
  readonly supplierId?: number | null;
  readonly description?: string | null;
  readonly amount?: string;
  readonly currencyCode?: string;
  readonly allocMethod?: AllocMethod;
  readonly isEstimate?: boolean;
  readonly documentRef?: string | null;
  readonly incurredDate?: IsoDate;
}

/**
 * Xarajat turi (`cost_types`) — `GET /api/org/cost-types`.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ `defaultAlloc` — MOLIYA QARORI, DASTURCHI TANLOVI EMAS                   ║
 * ║ (LANDED_COST §2, Q-LC-1).                                                ║
 * ║                                                                          ║
 * ║ Bu maydon serverdan keladi va u LANDED_COST §2 dagi tavsiya jadvalining   ║
 * ║ O'ZI. Ilgari bu yerda `RECOMMENDED_ALLOC` degan nusxa turardi; u          ║
 * ║ O'CHIRILDI, chunki ikkita ro'yxat ertami-kechmi bir-biridan farq qiladi   ║
 * ║ va o'shanda ekran serverdan boshqa narsani tavsiya qilib turardi.        ║
 * ║ Nizo bo'lsa haqiqat SERVERDA: `allocMethod` berilmasa u baribir           ║
 * ║ `cost_types.default_alloc` ni qo'llaydi.                                  ║
 * ║                                                                          ║
 * ║ LED uchun eng qimmat xato: dengiz yukini `volume` o'rniga `weight`        ║
 * ║ bo'yicha taqsimlash. Namunaviy konteynerda u $448 ni panel'dan olib       ║
 * ║ lampa va lentaga ko'chiradi (§6.6) — jonli tekshirilgan.                  ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * Ruxsat — `shipmentcost.view`: bu xarid ma'lumotnomasi, «hammaga ochiq
 * ro'yxat» emas. Jonli tekshirilgan: admin va moliyachi 200, omborchi va
 * sotuvchi 403 — ya'ni endpoint xarajatlar tabi bilan AYNAN bir xil chegarada
 * va u tabni ochgan foydalanuvchi turlar ro'yxatini ham albatta oladi.
 */
export interface CostType {
  readonly id: number;
  readonly code: string;
  readonly name: string;
  /** `value | quantity | weight | volume | manual` — tavsiya etilgan usul. */
  readonly defaultAlloc: string;
}

export function fetchCostTypes(signal?: AbortSignal): Promise<readonly CostType[]> {
  return api.get<readonly CostType[]>('/org/cost-types', { ...(signal ? { signal } : {}) });
}

/**
 * Tavsiyani `AllocMethod` ga toraytiradi.
 *
 * Server `defaultAlloc` ni MATN sifatida qaytaradi. Ro'yxatdan tashqari qiymat
 * kelsa (yangi usul qo'shilgan, brauzer esa eski) — `undefined`, ya'ni ekran
 * tavsiyani KO'RSATMAYDI. Noma'lum qiymatni «value» deb ko'rsatish eng yomon
 * variant bo'lardi: u yolg'on tavsiya bo'lib chiqadi.
 */
export function recommendedAlloc(costType: CostType | undefined): AllocMethod | undefined {
  if (!costType) return undefined;
  return (ALLOC_METHOD_VALUES as readonly string[]).includes(costType.defaultAlloc)
    ? (costType.defaultAlloc as AllocMethod)
    : undefined;
}

// ---------------------------------------------------------------------------
// Landed cost
// ---------------------------------------------------------------------------

export const LC_RUN_TYPE_VALUES = ['estimate', 'final'] as const;
export type LcRunType = (typeof LC_RUN_TYPE_VALUES)[number];

export interface LandedCostAllocation {
  readonly shipmentCostId: number;
  readonly costName: string;
  readonly goodsReceiptLineId: number;
  readonly productId: number;
  readonly sku: string;
  readonly allocMethod: string;
  readonly allocBasis: string;
  readonly allocRatio: string;
  readonly allocatedBase: string;
  /** Yaxlitlash farqi shu satrga tushdimi (§3.4). Saqlangan run'da bo'lmaydi. */
  readonly absorbedRounding?: boolean;
}

export interface LandedCostLine {
  readonly goodsReceiptLineId: number;
  readonly productId: number;
  readonly sku: string;
  /** HAQIQATDA KELGAN miqdor — xarajat AYNAN shunga taqsimlanadi (§5). */
  readonly quantity: string;
  readonly expectedQuantity: string;
  readonly shortageQuantity: string | null;
  readonly claimStatus: string;
  readonly purchaseUnitCost: string;
  readonly landedUnitCost: string;
  readonly totalUnitCost: string;
  readonly purchaseTotal: string;
  readonly landedTotal: string;
  /** Xarajat tovar narxining necha foizi — §6.5 dagi «Landed %». */
  readonly landedPct: string;
}

export interface LandedCostView {
  readonly shipmentId: number;
  readonly shipmentDocNo: string;
  readonly runType: string;
  /** Tovar valyutasi va QABUL SANASI kursi (§3.3, Q-LC-3). */
  readonly purchaseCurrency: string;
  readonly purchaseRate: string;
  readonly receiptDate: IsoDate;
  readonly purchaseBase: string;
  readonly costsBase: string;
  readonly inventoryValue: string;
  readonly landedPct: string;
  readonly lines: readonly LandedCostLine[];
  readonly landedCostAllocations: readonly LandedCostAllocation[];
  readonly warnings: readonly string[];
}

export interface LandedCostRunResult extends LandedCostView {
  readonly runId: number;
  readonly runDate: IsoDate;
  readonly status: string;
  readonly reversalOfId: number | null;
  /** SOTILGAN ulushga to'g'ri kelgan farq — §4, Q-LC-5. Omborga QAYTMAYDI. */
  readonly cogsVarianceBase: string;
}

export interface LandedCostRunSummary {
  readonly id: number;
  readonly runType: string;
  readonly runDate: IsoDate;
  readonly status: string;
  readonly postedAt: string | null;
  readonly reversalOfId: number | null;
  readonly notes: string | null;
  readonly totalCostBase: string;
  readonly landedCostAllocations: readonly LandedCostAllocation[];
}

export interface MethodOverride {
  readonly shipmentCostId: number;
  readonly allocMethod: AllocMethod;
}

/**
 * Hisoblab ko'rsatadi, HECH NARSA saqlamaydi.
 *
 * `methodOverrides` FAQAT shu yerda ishlaydi — «agar boshqa usul tanlansa nima
 * bo'lardi» savoliga javob (`landed-cost.dto.ts` izohi). Post qilingandan
 * keyin tuzatish teskarilash + qayta baholash degani, va oradagi har bir sotuv
 * noto'g'ri COGS bilan yozilib ulguradi.
 */
export function previewLandedCost(
  shipmentId: number,
  body: { readonly runType: LcRunType; readonly methodOverrides?: readonly MethodOverride[] },
  signal?: AbortSignal,
): Promise<LandedCostView> {
  return api.post<LandedCostView>(
    `/shipments/${shipmentId}/landed-cost/preview`,
    {
      runType: body.runType,
      ...(body.methodOverrides && body.methodOverrides.length > 0
        ? { methodOverrides: body.methodOverrides }
        : {}),
    },
    { ...(signal ? { signal } : {}) },
  );
}

/**
 * Taqsimlaydi, SAQLAYDI va ombor daftariga post qiladi — bitta tranzaksiyada.
 *
 * `methodOverrides` bu yerda YO'Q va ataylab yo'q: usul `shipment_costs.
 * alloc_method` da turadi va uni o'zgartirish uchun XARAJAT QATORI
 * tahrirlanadi (`shipmentcost.edit`).
 */
export function runLandedCost(
  shipmentId: number,
  body: { readonly runType: LcRunType; readonly runDate?: IsoDate; readonly notes?: string | null },
): Promise<LandedCostRunResult> {
  return api.post<LandedCostRunResult>(`/shipments/${shipmentId}/landed-cost/run`, {
    runType: body.runType,
    ...(body.runDate ? { runDate: body.runDate } : {}),
    ...(body.notes ? { notes: body.notes } : {}),
  });
}

export function fetchLandedCostRuns(
  shipmentId: number,
  signal?: AbortSignal,
): Promise<readonly LandedCostRunSummary[]> {
  return api.get<readonly LandedCostRunSummary[]>(`/shipments/${shipmentId}/landed-cost/runs`, {
    ...(signal ? { signal } : {}),
  });
}

export type LandedCostReverseResult = LandedCostRunSummary & {
  readonly cogsVarianceBase: string;
  readonly warnings: readonly string[];
};

/**
 * Teskarilash — `landedcost.reverse` (MATRIX §3.3: faqat admin va moliya).
 * Sabab MAJBURIY: tannarx o'zgaradi, demak marja va barcha hisobot ham.
 */
export function reverseLandedCostRun(
  runId: number,
  reason: string,
): Promise<LandedCostReverseResult> {
  return api.post<LandedCostReverseResult>(`/landed-cost-runs/${runId}/reverse`, { reason });
}

// ---------------------------------------------------------------------------
// Tovar qabuli (goods receipt)
// ---------------------------------------------------------------------------

/** `goods-receipts.dto.ts` → `SHORTAGE_REASON_VALUES`. */
export const SHORTAGE_REASON_VALUES = ['shortage', 'damaged', 'wrong_item', 'excess'] as const;
export type ShortageReason = (typeof SHORTAGE_REASON_VALUES)[number];

/**
 * `CLAIM_STATUS_VALUES` — kamomadning PULI qayerga ketishini shu belgilaydi
 * (LANDED_COST §5, Q-LC-9):
 *   accepted      → yetkazib beruvchi qaytaradi, xarajat emas
 *   rejected/none → DAVR ZARARIGA, tannarxga emas
 */
export const CLAIM_STATUS_VALUES = [
  'none',
  'pending',
  'sent',
  'accepted',
  'rejected',
  'closed',
] as const;
export type ClaimStatus = (typeof CLAIM_STATUS_VALUES)[number];

export interface GoodsReceiptLine {
  readonly id: number;
  readonly productId: number;
  readonly purchaseOrderLineId: number | null;
  readonly sku: string;
  readonly name: string;
  readonly uomCode: string | null;
  readonly expectedQuantity: string;
  readonly quantity: string;
  readonly shortageQuantity: string | null;
  readonly shortageReason: string | null;
  readonly claimStatus: string;
  readonly claimAmount: string | null;
  readonly claimNote: string | null;
  readonly lotNo: string | null;
  readonly serialNo: string | null;
  readonly inventoryTxnId: number | null;
  /** FAQAT `product.cost.view` — omborchi tannarxni ko'rmaydi (MATRIX §3.2). */
  readonly purchaseUnitCost?: string;
  readonly landedUnitCost?: string;
  readonly totalUnitCost?: string;
}

export interface GoodsReceiptHeader {
  readonly id: number;
  readonly docNo: string;
  readonly shipmentId: number | null;
  readonly purchaseOrderId: number | null;
  readonly warehouseId: number;
  readonly warehouseCode: string | null;
  /** CNY→USD konvertatsiyasining KURS SANASI (§3.3, Q-LC-3). */
  readonly receiptDate: IsoDate;
  readonly status: string;
  readonly postedAt: string | null;
  readonly notes: string | null;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface GoodsReceipt extends GoodsReceiptHeader {
  readonly lines: readonly GoodsReceiptLine[];
  /** Bloklamaydigan ogohlantirishlar (TZ §28). */
  readonly warnings: readonly string[];
}

export interface GoodsReceiptListParams extends BaseListParams {
  readonly shipmentId?: number | undefined;
  readonly purchaseOrderId?: number | undefined;
  readonly warehouseId?: number | undefined;
  readonly status?: 'draft' | 'posted' | 'cancelled' | undefined;
  readonly includeCancelled?: boolean | undefined;
}

export function fetchGoodsReceipts(
  params: GoodsReceiptListParams,
  signal?: AbortSignal,
): Promise<Page<GoodsReceiptHeader>> {
  return api.get<Page<GoodsReceiptHeader>>('/goods-receipts', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy,
      sortDir: params.sortDir,
      q: params.q,
      shipmentId: params.shipmentId,
      purchaseOrderId: params.purchaseOrderId,
      warehouseId: params.warehouseId,
      status: params.status,
      from: params.from,
      to: params.to,
      includeCancelled: params.includeCancelled === true ? 'true' : undefined,
    },
    ...(signal ? { signal } : {}),
  });
}

export function fetchGoodsReceipt(id: number, signal?: AbortSignal): Promise<GoodsReceipt> {
  return api.get<GoodsReceipt>(`/goods-receipts/${id}`, { ...(signal ? { signal } : {}) });
}

/**
 * `purchaseOrderLineId` MAJBURIY: tovar narxi (CNY) aynan PO satridan olinadi —
 * `goods_receipt_lines` da hujjat valyutasidagi narx ustuni YO'Q. PO satrisiz
 * kelgan tovarning tannarxini hisoblab bo'lmaydi va landed cost run to'xtaydi.
 */
export interface GoodsReceiptLineInput {
  readonly productId: number;
  readonly purchaseOrderLineId: number;
  readonly expectedQuantity: string;
  /** HAQIQATDA kelgan miqdor — xarajat SHUNGA taqsimlanadi. */
  readonly quantity: string;
  readonly lotNo?: string | null;
  readonly shortageReason?: ShortageReason | null;
  readonly claimStatus?: ClaimStatus;
  readonly claimAmount?: string | null;
  readonly claimNote?: string | null;
}

export interface GoodsReceiptCreateInput {
  readonly shipmentId?: number | null;
  readonly purchaseOrderId?: number | null;
  readonly warehouseId: number;
  readonly receiptDate: IsoDate;
  readonly notes?: string | null;
  readonly lines: readonly GoodsReceiptLineInput[];
}

export function createGoodsReceipt(input: GoodsReceiptCreateInput): Promise<GoodsReceipt> {
  return api.post<GoodsReceipt>('/goods-receipts', input);
}

export function postGoodsReceipt(id: number, notes?: string | null): Promise<GoodsReceipt> {
  return api.post<GoodsReceipt>(`/goods-receipts/${id}/post`, notes ? { notes } : {});
}

// ---------------------------------------------------------------------------
// Ta'minotchilar — PO va konteyner formasidagi tanlagich uchun
// ---------------------------------------------------------------------------

export interface SupplierRow {
  readonly id: number;
  readonly code: string;
  readonly name: string;
  readonly country: string | null;
  /**
   * KONTRAGENT TURI — 0054.
   *
   * `?:` chunki u serverning TOR oq ro'yxatida yo'q (`SUPPLIER_PUBLIC_COLUMNS`):
   * `supplier.view` bo'lmasa kalit UMUMAN kelmaydi (`lib/suppliers.ts` dagi
   * bir xil qoida). Tanlagich uni faqat YORLIQ uchun ishlatadi — ajratishning
   * o'zi serverda, `?kind=` filtri bilan bo'ladi.
   */
  readonly kind?: SupplierKind;
  /** Default `CNY`: hisob Xitoy zavodlari bilan yuan'da yuritiladi. */
  readonly defaultCurrency: string;
  /*
   * Tijorat shartlari: `supplier.view` bo'lmasa so'rovga umuman kirmaydi
   * (`suppliers.service.ts` dagi `supplierColumns()`). Omborchi ta'minotchi
   * nomini ko'radi, shartini yo'q — MATRIX §3.1 izoh³.
   */
  readonly paymentTermDays?: number;
  readonly leadTimeDays?: number;
  readonly isActive: boolean;
}

const SUPPLIER_PICKER_LIMIT = 100;

/**
 * `supplier.view` talab qiladi — sotuvchida u YO'Q (MATRIX §3.1).
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ AJRATISH SERVERDA, BRAUZERDA EMAS (0054)                                 ║
 * ║                                                                          ║
 * ║ `kind` filtri so'rovga qo'shiladi va RO'YXATNI TO'LIQ olib kelib keyin   ║
 * ║ `filter()` qilish RAD ETILDI. Sabab sahifa chegarasida: yuqoridagi       ║
 * ║ chegara 100 ta va u NOMGA qarab saralangan — bitta ekspeditor 101-o'rinda║
 * ║ qolsa, u tanlagichda UMUMAN ko'rinmasdi va sabab hech qayerda            ║
 * ║ yozilmasdi. Server filtri esa chegarani o'z turi ichida qo'llaydi.       ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export function fetchSuppliers(
  params: { readonly kind?: SupplierKind } = {},
  signal?: AbortSignal,
): Promise<Page<SupplierRow>> {
  return api.get<Page<SupplierRow>>('/suppliers', {
    query: {
      pageSize: SUPPLIER_PICKER_LIMIT,
      sortBy: 'name',
      sortDir: 'asc',
      isActive: 'true',
      kind: params.kind,
    },
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// Query kalitlari
// ---------------------------------------------------------------------------

export const procurementKeys = {
  all: ['procurement'] as const,
  purchaseOrders: (params: PurchaseOrderListParams) =>
    ['procurement', 'purchase-orders', params] as const,
  purchaseOrder: (id: number) => ['procurement', 'purchase-order', id] as const,
  shipments: (params: ShipmentListParams) => ['procurement', 'shipments', params] as const,
  shipment: (id: number) => ['procurement', 'shipment', id] as const,
  shipmentCosts: (id: number) => ['procurement', 'shipment-costs', id] as const,
  costTypes: ['procurement', 'cost-types'] as const,
  landedCostRuns: (id: number) => ['procurement', 'landed-cost-runs', id] as const,
  goodsReceipts: (params: GoodsReceiptListParams) =>
    ['procurement', 'goods-receipts', params] as const,
  goodsReceipt: (id: number) => ['procurement', 'goods-receipt', id] as const,
  /*
   * Kalit TURNI o'z ichiga oladi: `kind=logistics` bilan olingan qisqa
   * ro'yxat filtrsiz so'rovning keshini ustidan yozib yuborardi va
   * xarid buyurtmasi formasi zavodlarni yo'qotardi.
   */
  suppliers: (kind?: SupplierKind) => ['procurement', 'suppliers', kind ?? 'all'] as const,
  paymentTerms: (id: number) => ['procurement', 'payment-terms', id] as const,
  workspace: ['procurement', 'workspace'] as const,
};

// ---------------------------------------------------------------------------
// Yordamchi hisoblar
// ---------------------------------------------------------------------------

/**
 * ETA va ATA orasidagi kun farqi.
 *
 * Musbat — kechikdi, manfiy — erta keldi. Bu ta'minotchi ishonchliligining
 * yagona o'lchovi va u 120-150 kunlik lead time rejasiga bevosita ta'sir
 * qiladi (GAP_ANALYSIS §A4).
 *
 * Sana `YYYY-MM-DD` bo'lib keladi va `new Date()` uni UTC yarim tuni deb
 * o'qiydi — ikkalasi ham UTC bo'lgani uchun farq to'g'ri chiqadi
 * (`lib/format.ts` dagi bir kunlik siljish muammosi bu yerda yo'q).
 */
export function delayDays(eta: string | null, ata: string | null): number | null {
  if (!eta || !ata) return null;

  const from = Date.parse(`${eta.slice(0, 10)}T00:00:00Z`);
  const to = Date.parse(`${ata.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(to)) return null;

  return Math.round((to - from) / 86_400_000);
}
